import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import {
  Phone,
  MessageCircle,
  MapPin,
  Instagram,
  Facebook,
  Scissors,
  Clock,
  Star,
  ChevronRight,
  ArrowUpRight,
} from "lucide-react";

const PHONE_DISPLAY = "965 05 90 18";
const PHONE_E164 = "+34965059018";
const WHATSAPP_BASE = "https://wa.me/34965059018";
const WHATSAPP_DEFAULT =
  WHATSAPP_BASE +
  "?text=Hola%2C%20me%20gustar%C3%ADa%20reservar%20una%20cita%20en%20la%20barber%C3%ADa.";

const LOGO =
  "https://customer-assets.emergentagent.com/job_asensi-cuts/artifacts/vf2l1uf4_3c060cc4-00a9-4563-b096-5b295b9d5ffa.jpeg";

const GALLERY = [
  "https://customer-assets.emergentagent.com/job_asensi-cuts/artifacts/sobhq8cq_5371a0a3-aef4-42cb-81da-a99dc1d66243.jpeg",
  "https://customer-assets.emergentagent.com/job_asensi-cuts/artifacts/4hbm540m_29fa3825-9212-4ac2-aad1-e3bfd218243e.jpeg",
  "https://customer-assets.emergentagent.com/job_asensi-cuts/artifacts/5yyevrrt_be700db7-b2b1-41c2-8cac-c9f30cfa1d97.jpeg",
];

const SERVICES = [
  {
    name: "Corte",
    price: "16€",
    time: "Aprox. 30 min",
    desc: "Lavado, corte y acabado profesional.",
  },
  {
    name: "Barba",
    price: "10€",
    time: "Aprox. 30 min",
    desc: "Arreglo o afeitado con acabado perfecto.",
  },
  {
    name: "Corte + Barba",
    price: "25€",
    time: "Aprox. 45 min",
    desc: "Corte y barba en un solo servicio.",
    highlight: true,
  },
  {
    name: "Corte Infantil",
    price: "13€",
    time: "Aprox. 30 min",
    desc: "Corte para niños con atención personalizada.",
  },
  {
    name: "Lavado & Peinado",
    price: "6€",
    time: "Aprox. 15 min",
    desc: "Lavado y peinado con acabado profesional.",
  },
];

const SCHEDULE = [
  { day: "Lunes", hours: "10:00–13:30 · 16:30–20:00", idx: 1 },
  { day: "Martes", hours: "09:00–13:30 · 16:30–20:00", idx: 2 },
  { day: "Miércoles", hours: "09:00–13:30 · 16:30–20:00", idx: 3 },
  { day: "Jueves", hours: "09:00–13:30 · 16:30–20:00", idx: 4 },
  { day: "Viernes", hours: "09:00–13:30 · 16:30–20:00", idx: 5 },
  { day: "Sábado", hours: "09:00–13:30", idx: 6 },
  { day: "Domingo", hours: "Cerrado", idx: 0, closed: true },
];

const STAR_KEYS = ["s1", "s2", "s3", "s4", "s5"];

const TESTIMONIALS = [
  {
    id: "carlos-m",
    text: "Atención excelente y resultado impecable. Mi barbería de referencia en Alicante.",
    author: "Carlos M.",
  },
  {
    id: "javier-r",
    text: "El mejor degradado que me han hecho. Profesionalidad y trato cercano.",
    author: "Javier R.",
  },
  {
    id: "adrian-g",
    text: "Ambiente increíble, productos premium y siempre puntuales. 10/10.",
    author: "Adrián G.",
  },
];

const waLink = (msg) =>
  `${WHATSAPP_BASE}?text=${encodeURIComponent(msg)}`;

const Eyebrow = ({ children }) => (
  <span className="inline-flex items-center gap-2 uppercase tracking-eyebrow text-[11px] text-[#c79c59]">
    <span className="block w-8 h-px bg-[#c79c59]" />
    {children}
  </span>
);

const Navbar = () => (
  <header
    data-testid="navbar"
    className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-[#0d0b08]/70 border-b border-white/5"
  >
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
      <a href="#top" className="flex items-center gap-3" data-testid="nav-logo">
        <img
          src={LOGO}
          alt="Asensi"
          className="w-9 h-9 rounded-full object-cover ring-1 ring-[#c79c59]/40"
        />
        <div className="leading-tight">
          <div className="font-serif text-lg text-[#ece5d6]">Asensi</div>
          <div className="text-[10px] tracking-eyebrow uppercase text-[#c79c59]">
            Barbería · Alicante
          </div>
        </div>
      </a>
      <nav className="hidden md:flex items-center gap-8 text-sm text-[#ece5d6]/80">
        <a href="#servicios" className="link-underline" data-testid="nav-servicios">
          Servicios
        </a>
        <a href="#horarios" className="link-underline" data-testid="nav-horarios">
          Horarios
        </a>
        <a href="#galeria" className="link-underline" data-testid="nav-galeria">
          Galería
        </a>
        <a href="#opiniones" className="link-underline" data-testid="nav-opiniones">
          Opiniones
        </a>
        <a href="#contacto" className="link-underline" data-testid="nav-contacto">
          Contacto
        </a>
      </nav>
      <a
        href={WHATSAPP_DEFAULT}
        target="_blank"
        rel="noopener noreferrer"
        className="btn-pill btn-primary text-sm"
        data-testid="nav-cta-reserve"
      >
        Reservar
        <ArrowUpRight className="w-4 h-4" />
      </a>
    </div>
  </header>
);

const Hero = () => (
  <section id="top" className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 vignette">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
      <div className="lg:col-span-7 fade-up">
        <Eyebrow>Alicante · Desde el corazón del barrio</Eyebrow>
        <h1
          className="font-serif text-[44px] leading-[1.02] sm:text-[68px] lg:text-[88px] mt-6 text-[#ece5d6]"
          data-testid="hero-title"
        >
          Tu estilo
          <br />
          <span className="italic text-[#c79c59]">empieza</span> aquí.
        </h1>
        <p className="mt-8 max-w-xl text-[#ece5d6]/70 text-base sm:text-lg leading-relaxed">
          Barbería Alejandro Asensi no es una barbería cualquiera: es la mejor
          experiencia de corte. Un lugar con energía, buen ambiente y trato de
          amigos.
        </p>
        <div className="mt-10 flex flex-wrap gap-4">
          <a
            href={`tel:${PHONE_E164}`}
            className="btn-pill btn-primary"
            data-testid="hero-call-btn"
          >
            <Phone className="w-4 h-4" />
            Llamar ahora
            <span className="opacity-70">{PHONE_DISPLAY}</span>
          </a>
          <a
            href={WHATSAPP_DEFAULT}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-pill btn-whatsapp"
            data-testid="hero-whatsapp-btn"
          >
            <MessageCircle className="w-4 h-4" />
            Reservar por WhatsApp
          </a>
        </div>

        <div className="mt-14 grid grid-cols-3 gap-6 max-w-lg">
          <div data-testid="stat-years">
            <div className="font-serif text-4xl text-[#c79c59]">+15</div>
            <div className="text-xs uppercase tracking-eyebrow text-[#ece5d6]/50 mt-2">
              Años de oficio
            </div>
          </div>
          <div data-testid="stat-rating">
            <div className="font-serif text-4xl text-[#c79c59]">4.9</div>
            <div className="text-xs uppercase tracking-eyebrow text-[#ece5d6]/50 mt-2">
              Valoración media
            </div>
          </div>
          <div data-testid="stat-clients">
            <div className="font-serif text-4xl text-[#c79c59]">2K+</div>
            <div className="text-xs uppercase tracking-eyebrow text-[#ece5d6]/50 mt-2">
              Clientes felices
            </div>
          </div>
        </div>
      </div>

      <div className="lg:col-span-5 fade-up" style={{ animationDelay: "120ms" }}>
        <div className="relative">
          <div className="absolute -inset-4 diagonal-stripes opacity-40 rounded-2xl" />
          <div className="relative rounded-2xl overflow-hidden border border-[#c79c59]/30 bg-[#15110c] aspect-[4/5] flex items-center justify-center p-8">
            <img
              src={LOGO}
              alt="Logo Peluquería Barbería Alejandro Asensi"
              className="w-full h-full object-contain"
              data-testid="hero-logo"
            />
          </div>
          <div className="absolute -bottom-6 -left-6 bg-[#c79c59] text-[#0d0b08] px-5 py-3 rounded-full font-serif italic text-sm rotate-[-6deg] shadow-xl hidden sm:block">
            Tradición & precisión
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Marquee = () => {
  const items = ["Corte", "Barba", "Degradados", "Afeitado clásico", "Lavado & Peinado", "Infantil"];
  const arr = [
    ...items.map((it) => ({ label: it, key: `a-${it}` })),
    ...items.map((it) => ({ label: it, key: `b-${it}` })),
    ...items.map((it) => ({ label: it, key: `c-${it}` })),
  ];
  return (
    <div className="border-y border-[#c79c59]/20 bg-[#0a0805] overflow-hidden py-5">
      <div className="marquee-track flex gap-12 whitespace-nowrap text-[#c79c59]/80 font-serif text-xl sm:text-2xl">
        {arr.map((it) => (
          <span key={it.key} className="flex items-center gap-12">
            {it.label}
            <Scissors className="w-4 h-4 text-[#c79c59]/40" />
          </span>
        ))}
      </div>
    </div>
  );
};

const Reserve = () => (
  <section id="reserva" className="py-24 sm:py-32">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-start">
      <div className="lg:col-span-5">
        <Eyebrow>Reserva tu cita</Eyebrow>
        <h2 className="font-serif text-4xl sm:text-5xl mt-5 text-[#ece5d6]">
          ¿Listo para tu próximo corte?
        </h2>
        <p className="mt-6 text-[#ece5d6]/70 max-w-md">
          Reserva en segundos. Llámanos directamente o escríbenos por WhatsApp
          y te confirmamos en el momento.
        </p>
      </div>
      <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
        <a
          href={`tel:${PHONE_E164}`}
          className="card-lift block p-7 rounded-xl border border-white/10 bg-[#15110c]"
          data-testid="reserve-call-card"
        >
          <div className="flex items-center justify-between">
            <div className="w-11 h-11 rounded-full bg-[#c79c59]/15 flex items-center justify-center">
              <Phone className="w-5 h-5 text-[#c79c59]" />
            </div>
            <ChevronRight className="w-5 h-5 text-[#c79c59]/60" />
          </div>
          <div className="font-serif text-2xl text-[#ece5d6] mt-6">
            Reserva por teléfono
          </div>
          <div className="text-sm text-[#ece5d6]/60 mt-2">
            Habla directamente con nosotros
          </div>
          <div className="text-[#c79c59] font-medium mt-4">{PHONE_DISPLAY}</div>
        </a>
        <a
          href={WHATSAPP_DEFAULT}
          target="_blank"
          rel="noopener noreferrer"
          className="card-lift block p-7 rounded-xl border border-white/10 bg-[#15110c]"
          data-testid="reserve-whatsapp-card"
        >
          <div className="flex items-center justify-between">
            <div className="w-11 h-11 rounded-full bg-[#1ea159]/20 flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-[#4ac87a]" />
            </div>
            <ChevronRight className="w-5 h-5 text-[#c79c59]/60" />
          </div>
          <div className="font-serif text-2xl text-[#ece5d6] mt-6">
            Reserva por WhatsApp
          </div>
          <div className="text-sm text-[#ece5d6]/60 mt-2">
            Respuesta rápida en chat
          </div>
          <div className="text-[#4ac87a] font-medium mt-4">Escribir ahora</div>
        </a>
      </div>
    </div>
  </section>
);

const Services = () => (
  <section id="servicios" className="py-24 sm:py-32 bg-[#0a0805]">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <div className="grid lg:grid-cols-12 gap-12 items-end">
        <div className="lg:col-span-7">
          <Eyebrow>Carta de servicios</Eyebrow>
          <h2 className="font-serif text-4xl sm:text-5xl mt-5 text-[#ece5d6]">
            Precisión en cada <span className="italic text-[#c79c59]">detalle</span>.
          </h2>
        </div>
        <div className="lg:col-span-5">
          <p className="text-[#ece5d6]/70">
            Servicios pensados para hombres exigentes. Productos premium y
            técnicas tradicionales con un acabado contemporáneo.
          </p>
        </div>
      </div>

      <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {SERVICES.map((s, i) => (
          <article
            key={s.name}
            data-testid={`service-card-${i}`}
            className={`card-lift relative p-7 rounded-xl border ${
              s.highlight
                ? "border-[#c79c59]/60 bg-gradient-to-b from-[#1c160d] to-[#15110c]"
                : "border-white/10 bg-[#15110c]"
            }`}
          >
            {s.highlight && (
              <span className="absolute top-5 right-5 text-[10px] uppercase tracking-eyebrow text-[#0d0b08] bg-[#c79c59] px-2 py-1 rounded-full">
                Top
              </span>
            )}
            <div className="flex items-start justify-between">
              <div className="font-serif text-3xl text-[#c79c59]">{s.price}</div>
              <div className="flex items-center gap-2 text-xs text-[#ece5d6]/50">
                <Clock className="w-3.5 h-3.5" />
                {s.time}
              </div>
            </div>
            <h3 className="font-serif text-2xl mt-6 text-[#ece5d6]">{s.name}</h3>
            <p className="text-sm text-[#ece5d6]/60 mt-2">{s.desc}</p>
            <div className="mt-6 flex gap-3">
              <a
                href={`tel:${PHONE_E164}`}
                className="btn-pill btn-outline text-xs px-4 py-2"
                data-testid={`service-call-${i}`}
              >
                <Phone className="w-3.5 h-3.5" />
                Llamar
              </a>
              <a
                href={waLink(
                  `Hola, me gustaría reservar: ${s.name} (${s.price}).`
                )}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-pill btn-whatsapp text-xs px-4 py-2"
                data-testid={`service-wa-${i}`}
              >
                <MessageCircle className="w-3.5 h-3.5" />
                WhatsApp
              </a>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
);

const getTodayIdx = () => new Date().getDay();

const Hours = () => {
  const today = getTodayIdx();
  return (
    <section id="horarios" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Eyebrow>Horarios</Eyebrow>
          <h2 className="font-serif text-4xl sm:text-5xl mt-5 text-[#ece5d6]">
            Te esperamos
            <br />
            <span className="italic text-[#c79c59]">cuando lo necesites.</span>
          </h2>
          <p className="mt-6 text-[#ece5d6]/70 max-w-md">
            ¿Quieres reservar fuera del horario? Déjanos un WhatsApp y te
            confirmamos lo antes posible.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={`tel:${PHONE_E164}`}
              className="btn-pill btn-outline text-sm"
              data-testid="hours-call-btn"
            >
              <Phone className="w-4 h-4" />
              Llamar ahora
            </a>
            <a
              href={WHATSAPP_DEFAULT}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-pill btn-whatsapp text-sm"
              data-testid="hours-whatsapp-btn"
            >
              <MessageCircle className="w-4 h-4" />
              Escríbenos por WhatsApp
            </a>
          </div>
        </div>

        <div className="lg:col-span-7">
          <ul className="divide-y divide-white/10 rounded-xl border border-white/10 overflow-hidden bg-[#15110c]">
            {SCHEDULE.map((d) => {
              const isToday = d.idx === today;
              return (
                <li
                  key={d.day}
                  data-testid={`schedule-${d.day.toLowerCase()}`}
                  className={`flex items-center justify-between px-6 py-5 transition-colors ${
                    isToday ? "bg-[#1c160d]" : ""
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <span className={d.closed ? "dot-closed" : "dot-live"} />
                    <span className="font-serif text-xl text-[#ece5d6]">
                      {d.day}
                    </span>
                    {isToday && (
                      <span className="text-[10px] uppercase tracking-eyebrow text-[#c79c59] border border-[#c79c59]/50 rounded-full px-2 py-0.5">
                        Hoy
                      </span>
                    )}
                  </div>
                  <span
                    className={`text-sm ${
                      d.closed ? "text-[#b14a4a]" : "text-[#ece5d6]/80"
                    }`}
                  >
                    {d.hours}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
};

const Gallery = () => (
  <section id="galeria" className="py-24 sm:py-32 bg-[#0a0805]">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <div className="grid lg:grid-cols-12 gap-12 items-end">
        <div className="lg:col-span-7">
          <Eyebrow>Galería</Eyebrow>
          <h2 className="font-serif text-4xl sm:text-5xl mt-5 text-[#ece5d6]">
            Cortes que <span className="italic text-[#c79c59]">hablan</span>.
          </h2>
        </div>
        <div className="lg:col-span-5">
          <p className="text-[#ece5d6]/70">
            Una pequeña selección de trabajos recientes. Estilo, técnica y
            carácter.
          </p>
        </div>
      </div>
      <div className="mt-12 grid md:grid-cols-3 gap-5">
        {GALLERY.map((src, i) => (
          <div
            key={src}
            data-testid={`gallery-item-${i}`}
            className="gallery-item aspect-[4/5]"
          >
            <img
              src={src}
              alt={`Trabajo ${i + 1}`}
              className="w-full h-full object-cover"
            />
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Testimonials = () => (
  <section id="opiniones" className="py-24 sm:py-32">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <div className="grid lg:grid-cols-12 gap-12 items-end">
        <div className="lg:col-span-7">
          <Eyebrow>Opiniones</Eyebrow>
          <h2 className="font-serif text-4xl sm:text-5xl mt-5 text-[#ece5d6]">
            Lo que dicen los <span className="italic text-[#c79c59]">clientes</span>.
          </h2>
        </div>
      </div>
      <div className="mt-12 grid md:grid-cols-3 gap-5">
        {TESTIMONIALS.map((t, i) => (
          <figure
            key={t.id}
            data-testid={`testimonial-${i}`}
            className="card-lift p-7 rounded-xl border border-white/10 bg-[#15110c]"
          >
            <div className="flex gap-1 text-[#c79c59]">
              {STAR_KEYS.map((sk) => (
                <Star key={`${t.id}-${sk}`} className="w-4 h-4 fill-[#c79c59] stroke-none" />
              ))}
            </div>
            <blockquote className="font-serif text-xl text-[#ece5d6] mt-5 leading-snug">
              “{t.text}”
            </blockquote>
            <figcaption className="text-sm text-[#c79c59] mt-6">
              — {t.author}
            </figcaption>
          </figure>
        ))}
      </div>
    </div>
  </section>
);

const ContactCard = ({ icon: Icon, iconColor, label, children }) => (
  <div className="card-lift p-7 rounded-xl border border-white/10 bg-[#15110c]">
    <Icon className={`w-6 h-6 ${iconColor}`} />
    <div className="text-xs uppercase tracking-eyebrow text-[#ece5d6]/50 mt-6">
      {label}
    </div>
    {children}
  </div>
);

const SocialCard = () => (
  <div className="lg:col-span-5 p-7 rounded-xl border border-white/10 bg-[#15110c]">
    <div className="text-xs uppercase tracking-eyebrow text-[#ece5d6]/50">
      Redes sociales
    </div>
    <div className="flex flex-wrap gap-3 mt-5">
      <a
        href="https://www.facebook.com/search/top?q=peluquer%C3%ADa%20barber%C3%ADa%20alejandro%20asensi"
        target="_blank"
        rel="noopener noreferrer"
        className="btn-pill btn-outline text-sm"
        data-testid="social-facebook"
      >
        <Facebook className="w-4 h-4" />
        Facebook
      </a>
      <a
        href="https://www.instagram.com/asensialejandro?igsh=YXBuZDM3enN1aDlk"
        target="_blank"
        rel="noopener noreferrer"
        className="btn-pill btn-outline text-sm"
        data-testid="social-instagram"
      >
        <Instagram className="w-4 h-4" />
        @asensialejandro
      </a>
    </div>
  </div>
);

const QuickBookCard = () => (
  <div className="lg:col-span-7 p-7 rounded-xl border border-[#c79c59]/40 bg-gradient-to-br from-[#1c160d] to-[#15110c] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
    <div>
      <div className="font-serif text-2xl text-[#ece5d6]">
        Reserva en 30 segundos
      </div>
      <p className="text-sm text-[#ece5d6]/65 mt-2 max-w-md">
        Llámanos o escríbenos por WhatsApp. Te confirmamos tu cita al
        momento.
      </p>
    </div>
    <div className="flex flex-wrap gap-3">
      <a
        href={`tel:${PHONE_E164}`}
        className="btn-pill btn-outline text-sm"
        data-testid="contact-cta-call"
      >
        <Phone className="w-4 h-4" />
        {PHONE_DISPLAY}
      </a>
      <a
        href={WHATSAPP_DEFAULT}
        target="_blank"
        rel="noopener noreferrer"
        className="btn-pill btn-whatsapp text-sm"
        data-testid="contact-cta-whatsapp"
      >
        <MessageCircle className="w-4 h-4" />
        WhatsApp
      </a>
    </div>
  </div>
);

const Contact = () => (
  <section id="contacto" className="py-24 sm:py-32 bg-[#0a0805]">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <div className="grid lg:grid-cols-12 gap-12 items-end">
        <div className="lg:col-span-7">
          <Eyebrow>Contacto</Eyebrow>
          <h2 className="font-serif text-4xl sm:text-5xl mt-5 text-[#ece5d6]">
            Ven a <span className="italic text-[#c79c59]">vernos</span>.
          </h2>
        </div>
      </div>

      <div className="mt-12 grid lg:grid-cols-3 gap-5">
        <ContactCard icon={Phone} iconColor="text-[#c79c59]" label="Teléfono">
          <a
            href={`tel:${PHONE_E164}`}
            className="font-serif text-2xl text-[#ece5d6] mt-2 block link-underline"
            data-testid="contact-phone"
          >
            {PHONE_DISPLAY}
          </a>
          <div className="text-sm text-[#ece5d6]/60 mt-2">Pulsa para llamar</div>
        </ContactCard>

        <ContactCard icon={MessageCircle} iconColor="text-[#4ac87a]" label="WhatsApp">
          <a
            href={WHATSAPP_DEFAULT}
            target="_blank"
            rel="noopener noreferrer"
            className="font-serif text-2xl text-[#ece5d6] mt-2 block link-underline"
            data-testid="contact-whatsapp"
          >
            Escríbenos
          </a>
          <div className="text-sm text-[#ece5d6]/60 mt-2">Reserva por chat</div>
        </ContactCard>

        <ContactCard icon={MapPin} iconColor="text-[#c79c59]" label="Dirección">
          <p className="font-serif text-xl text-[#ece5d6] mt-2 leading-snug">
            Calle Sacerdote Isidro Albert, 20, Local 4
          </p>
          <p className="text-sm text-[#ece5d6]/60 mt-1">03010 Alicante, España</p>
          <a
            href="https://www.google.com/maps/search/?api=1&query=Calle%20Sacerdote%20Isidro%20Albert%2C%2020%2C%20Local%204%2003010%20Alicante"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 mt-4 text-[#c79c59] text-sm link-underline"
            data-testid="contact-map"
          >
            Cómo llegar →
          </a>
        </ContactCard>
      </div>

      <div className="mt-10 grid lg:grid-cols-12 gap-5">
        <SocialCard />
        <QuickBookCard />
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer className="border-t border-white/5 py-10 bg-[#0a0805]">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-6">
      <div className="flex items-center gap-3">
        <img
          src={LOGO}
          alt="Asensi"
          className="w-9 h-9 rounded-full object-cover ring-1 ring-[#c79c59]/40"
        />
        <div>
          <div className="font-serif text-[#ece5d6]">Barbería Alejandro Asensi</div>
          <div className="text-xs text-[#ece5d6]/50">© {new Date().getFullYear()} · Alicante, España</div>
        </div>
      </div>
      <div className="flex gap-3">
        <a
          href={WHATSAPP_DEFAULT}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-pill btn-whatsapp text-xs px-4 py-2"
          data-testid="footer-whatsapp"
        >
          Reservar por WhatsApp
        </a>
        <a
          href={`tel:${PHONE_E164}`}
          className="btn-pill btn-outline text-xs px-4 py-2"
          data-testid="footer-call"
        >
          Llamar ahora
        </a>
      </div>
    </div>
  </footer>
);

/* Floating mobile-friendly WhatsApp button */
const FloatingWhats = () => (
  <a
    href={WHATSAPP_DEFAULT}
    target="_blank"
    rel="noopener noreferrer"
    className="fixed bottom-20 right-5 z-40 w-14 h-14 rounded-full bg-[#1ea159] hover:bg-[#23b766] flex items-center justify-center shadow-2xl transition-transform hover:scale-110"
    data-testid="floating-whatsapp"
    aria-label="WhatsApp"
  >
    <MessageCircle className="w-6 h-6 text-white" />
  </a>
);

const Home = () => (
  <div className="grain">
    <Navbar />
    <main>
      <Hero />
      <Marquee />
      <Reserve />
      <Services />
      <Hours />
      <Gallery />
      <Testimonials />
      <Contact />
    </main>
    <Footer />
    <FloatingWhats />
  </div>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
